import React from "react";
import { useHistory } from "react-router";
import { useUnmountEffect } from "framer-motion";

import { IS_BROWSER } from "@config/consts";

import { Drawer } from "@ui/Molecules";

import { iOS } from "@services/iOS";

import { GenericPortal } from "@containers/GenericPortal";

import { useMatchPath } from "@hooks/useMatchPath";

import { DrawerManagerProvider } from "./DrawerManager.context";
import { useManager } from "./DrawerManager.hooks";
import { DrawerManagerComponent } from "./DrawerManager.types";

export const DrawerManager: DrawerManagerComponent = ({
  id,
  url,
  preventIOSKeyboardScroll,
  onClose,
  ...props
}) => {
  const matchPath = useMatchPath();
  const history = useHistory();

  const drawer = useManager();
  const isActive = drawer.isDrawerActive(id);

  React.useEffect(() => {
    !!(url && matchPath(url)) && drawer.showDrawer(id);

    drawer.registerCloseHandler(id, () => {
      url &&
        matchPath(url) &&
        history.replace(history.location.pathname.replace(/\/[^/]+\/?$/, "/"));

      onClose?.();
    });
  }, [onClose, matchPath, history]);

  // fix for iOS issue (DEV-311)
  // https://blog.opendigerati.com/the-eccentric-ways-of-ios-safari-with-the-keyboard-b5aa3f34228d
  React.useEffect(() => {
    if (!IS_BROWSER) return;
    if (!preventIOSKeyboardScroll) return;
    if (!iOS()) return;

    const scrollHandler = (event: Event): void => {
      const target = event.currentTarget as Window;
      const { scrollY } = target;

      // for a moment scrollY can be "0"
      if (scrollY !== 0) {
        target.removeEventListener("scroll", scrollHandler);

        window.scrollTo(0, 0);
      }
    };

    const cleanListener = () =>
      window.removeEventListener("scroll", scrollHandler);

    if (isActive) {
      window.addEventListener("scroll", scrollHandler);
      setTimeout(cleanListener, 500);
    } else {
      cleanListener;
    }

    return cleanListener;
  }, [preventIOSKeyboardScroll, isActive]);

  useUnmountEffect(() => {
    drawer.hideDrawer(id);
  });

  return (
    <GenericPortal.Portal type="drawer">
      {isActive && (
        <Drawer onRequestClose={() => drawer.hideDrawer(id)} {...props} />
      )}
    </GenericPortal.Portal>
  );
};

DrawerManager.Provider = DrawerManagerProvider;
DrawerManager.useManager = useManager;
